import { forwardRef, Ref, useMemo } from 'react';

import { Shadow } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { GetProps, isWeb, Stack, styled, TamaguiElement } from '@tamagui/core';

import { getShadowProps } from '../../helpers/tokens';
import { createAnchorOrButtonFrame } from '../shared/anchorOrButton';

const sharedConfig = {
  name: 'PressableBox',

  variants: {
    shadow: (shadowToken: Shadow) => {
      return getShadowProps(shadowToken);
    },

    disabled: {
      true: {
        pointerEvents: 'none',
      },
    },
  } as const,
};

const ButtonFrame = styled(Stack, {
  ...sharedConfig,
  tag: 'button',
  role: 'button',

  cursor: 'pointer',
  userSelect: 'none',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  borderWidth: 0,
  backgroundColor: '$transparent',
  borderColor: '$transparent',
  // @ts-expect-error - web-only
  type: 'button',
});

type ButtonFrameProps = GetProps<typeof ButtonFrame>;

const AnchorFrame = styled(Stack, {
  ...sharedConfig,
  tag: 'a',
  accessibilityRole: 'link',
  display: 'flex',
  position: 'relative',
});

type AnchorFrameProps = GetProps<typeof AnchorFrame>;

const AnchorOrButtonFrame = createAnchorOrButtonFrame<AnchorFrameProps | ButtonFrameProps>({
  AnchorFrame,
  ButtonFrame,
});

type AnchorOrButtonFrameProps = GetProps<typeof AnchorOrButtonFrame>;

export type PressableBoxProps = AnchorOrButtonFrameProps &
  // Requiring aria-label because there's a somewhat high chance the component
  // will be used without visible text
  Required<Pick<AnchorOrButtonFrameProps, 'aria-label'>>;

/**
 * `PressableBox` is a container component that can be used to make children
 * "pressable" in an accessible way. By default, it will render as a button,
 * but if you pass an `href` prop, it will render as a link instead. Depending
 * on whether it is used as a button or a link, it will track "button" or
 * "link" analytics events when you pass `analyticsName` and `trackImpression`
 * props. It is required to pass `aria-label` to make sure the component
 * is accessible. If you are absolutely sure that the box already contains
 * visible text that is accessible, you can pass an empty string to
 * `aria-label` and the attribute will be omitted from the DOM.
 */
export const PressableBox = forwardRef(function PressableBox(
  { children, 'aria-label': ariaLabel, style: styleProp, ...rest }: PressableBoxProps,
  ref: Ref<TamaguiElement>,
) {
  // On mobile safari, sometimes there's a flash of gray when the user taps on
  // the pressable box. Setting '-webkit-tap-highlight-color' to transparent
  // removes the flash. Consider moving this to global css for all buttons/links
  const style = useMemo(() => {
    if (isWeb) {
      const result = styleProp && typeof styleProp === 'object' ? styleProp : {};
      // @ts-expect-error - web-only style prop
      result.WebkitTapHighlightColor = 'transparent';
      return result;
    }
    return styleProp;
  }, [styleProp]);

  return (
    <AnchorOrButtonFrame ref={ref} aria-label={ariaLabel || undefined} style={style} {...rest}>
      {children}
    </AnchorOrButtonFrame>
  );
});
